// @ts-nocheck

import { filter, flatten, isEmpty, map, sortBy, uniq } from 'lodash';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BoxV2, ButtonUI, Selector, showNotification, usePrevious } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { worldTimezones } from 'sonata-static-storage/lib/samples/worldTimezones.samples';

import {
  getInitialTimeFrame,
  getNewTimeFrame,
  getTimeStepForTimeFrame,
  getVariableFilters
} from '@/helpers/reporting.helpers';
import { getVariableValue } from '@/modules/reporting/reporting.resources';
import {
  reportAdvancedFilterApplySettings,
  reportAdvancedFilterSetSettings
} from '@/store/actions/reportingAdvancedFilter.actions';
import {
  getCountriesWithEvents,
  getHasFootfallAttribution,
  getSelectedCampaigns
} from '@/store/selectors/reporting.selectors';
import { getCampaigns, getSection } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getBaseCurrencyCatalog, getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { AdvancedFilterDatePicker } from './AdvancedFilterDatePicker';
import {
  formatTimeFrame,
  getCommonCountryLevels,
  getSelectorValue,
  isDisabledCartographyLevelSelector,
  timeRangeOptionIds
} from './helpers';
import './reportingSectionAdvancedFilter.scss';

const normalizeAndSortCartographyLevels = (items, keys = {}) =>
  items
    .map(option => ({
      id: keys.id ? option[keys.id] : option,
      label: keys.label ? option[keys.label] : option
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const formatAndSortVariableValues = (variableValues, currentVariableValues) => {
  return Object.entries(variableValues).reduce((acc, [valueKey, value]) => {
    if (currentVariableValues.includes(valueKey)) {
      return {
        ...acc,
        [valueKey]: value
      };
    }
    return acc;
  }, {});
};

const getSectionDefaultCartographyLevel = (section, generalSectionCartographyLevel, sectionCartographyLevel) => {
  if (section === 'general' || generalSectionCartographyLevel !== sectionCartographyLevel) {
    return sectionCartographyLevel;
  }
  return '';
};

const getSectionDefaultCartographyLevelIds = (
  section,
  generalSectionCartographyLevel,
  sectionCartographyLevel,
  sectionCartographyLevelIds
) => {
  if (section === 'general' || generalSectionCartographyLevel !== sectionCartographyLevel) {
    return sectionCartographyLevelIds;
  }
  return [];
};
function ReportingSectionAdvancedFilter(
  { className = '', enabled = false, initialSettings, isModal = false, onSaveButtonDisable, section = 'general' },
  ref
) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const baseCurrencyCatalog = useSelector(getBaseCurrencyCatalog);
  const cids = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const countries = useSelector(getCountriesWithEvents) || [];
  const currentSection = useSelector(store => getSection(store, section));
  const generalSectionSettings = useSelector(store => getSection(store, 'general'));
  const hasFootfallAttribution = useSelector(getHasFootfallAttribution);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const initialDates = getInitialTimeFrame(reportingCampaigns.filter(({ id }) => cids.includes(id)));

  const { formatMessage } = useIntl();
  const [
    {
      cartographyCountryOptions,
      cartographyItemOptions,
      cartographyLevelOptions,
      countriesInfo,
      currentBeginDate,
      currentCartographyCountries,
      currentCartographyLevel,
      currentCartographyLevelIds,
      currentChartTimeStep,
      currentCurrency,
      currentEndDate,
      currentTablesTimeStep,
      currentTimeFrame,
      currentTimeRange,
      currentTimeZone,
      currentVariableType,
      currentVariableValues,
      variableValuesOptions
    },
    setState
  ] = useState({
    cartographyCountryOptions: [],
    cartographyItemOptions: [],
    cartographyLevelOptions: [],
    countriesInfo: undefined,
    currentBeginDate: currentSection.timeFrame.beginDate,
    currentCartographyCountries: currentSection.cartographyCountries,
    currentCartographyLevel: getSectionDefaultCartographyLevel(
      section,
      generalSectionSettings.cartographyLevel,
      currentSection.cartographyLevel
    ),
    currentCartographyLevelIds: getSectionDefaultCartographyLevelIds(
      section,
      generalSectionSettings.cartographyLevel,
      currentSection.cartographyLevel,
      currentSection.cartographyLevelIds
    ),
    currentChartTimeStep: currentSection.chartTimeStep,
    currentCurrency: currentSection.currency,
    currentEndDate: currentSection.timeFrame.endDate,
    currentTablesTimeStep: currentSection.tablesTimeStep,
    currentTimeFrame: currentSection.timeFrame,
    currentTimeRange: currentSection.timeRange,
    currentTimeZone: currentSection.timeZone,
    currentVariableType: currentSection.variableType,
    currentVariableValues: currentSection.variableValues,
    variableValuesOptions: []
  });

  const showDatePicker = currentTimeRange === 'customDates';
  const cartographyPermission = checkUserResourcePermissions('reportingFilterCartographyLevels', user, userRoles);
  const variableSelectorsPermission = checkUserResourcePermissions(
    'reportingAdvancedFilterByVariable',
    user,
    userRoles
  );
  const demographyToken = {};
  const cartographyLevelToken = {};
  const variableToken = {};

  const multiCountryReport = countries.length > 1;

  const fetchCartographyCountrySelectorOptions = async () => {
    try {
      const countriesInfoResponse = await getCartographyDictionary(countries, demographyToken);
      const options = countriesInfoResponse.map(option => {
        return {
          id: option.id,
          label: option.name
        };
      });
      setState(prevState => ({
        ...prevState,
        cartographyCountryOptions: options,
        countriesInfo: countriesInfoResponse
      }));
      fetchCartographyLevelSelectorOptions(
        !multiCountryReport ? countries : currentCartographyCountries,
        countriesInfoResponse
      );
    } catch (e) {
      console.info(e.message);
    }
  };

  useEffect(() => {
    fetchCartographyCountrySelectorOptions();
    !!currentCartographyLevel.length &&
      fetchCartographyLevelShapes(
        currentCartographyLevel,
        multiCountryReport ? currentCartographyCountries : countries
      );
    currentVariableType && fetchVariableValuesOptions(currentVariableType);
    return () => {
      demographyToken.cancel && demographyToken.cancel();
      cartographyLevelToken.cancel && cartographyLevelToken.cancel();
      variableToken.cancel && variableToken.cancel();
    };
  }, []);

  const currencyOptions = () => {
    if (baseCurrencyCatalog.length === 0) {
      return [
        {
          id: 0,
          label: 'Loading...'
        }
      ];
    }
    return baseCurrencyCatalog.map(option => ({
      id: option.baseCurrency,
      label: option.copy
    }));
  };

  const timeZoneOptions = () => {
    const allTimeZones = sortBy(
      uniq(
        flatten(
          map(
            filter(worldTimezones, timezoneGroup => {
              return timezoneGroup.group !== 'UTC' && timezoneGroup.group !== 'Manual Offsets';
            }),
            'zones'
          )
        )
      ),
      'name'
    );
    return allTimeZones.map(zone => ({
      id: zone.value,
      label: zone.name
    }));
  };

  const timeRangeOptions = () => {
    let optionIdsFiltered = [];
    if (section !== 'general') {
      optionIdsFiltered = timeRangeOptionIds.filter(option =>
        [generalSectionSettings.timeRange, 'customDates'].includes(option)
      );
    } else {
      optionIdsFiltered = timeRangeOptionIds;
    }
    return optionIdsFiltered.map(optionId => {
      return {
        id: optionId,
        label: formatMessage({
          defaultMessage: optionId,
          id: `reporting.advancedFilters.date.options.${optionId}`
        })
      };
    });
  };

  const statsFilters = () => ({
    ...(section !== 'general' &&
      generalSectionSettings.cartographyLevelIds.length && {
        cartography: {
          [generalSectionSettings.cartographyLevel]: generalSectionSettings.cartographyLevelIds
        }
      }),
    ...(section !== 'general' && {
      end: generalSectionSettings.timeFrame.endDate,
      start: generalSectionSettings.timeFrame.beginDate,
      timezone: generalSectionSettings.timeZone
    }),
    timezone: currentTimeZone
  });

  const fetchCartographyLevelSelectorOptions = (selectedCountries, countriesInfoResponse) => {
    if (isDisabledCartographyLevelSelector(selectedCountries)) {
      return;
    }
    try {
      const filteredOptions = getCommonCountryLevels(
        (countriesInfoResponse || countriesInfo).filter(
          ({ id }) => !multiCountryReport || selectedCountries.includes(id)
        )
      );
      let newOptions = filteredOptions.map(option => ({
        id: option.id,
        label: option.name
      }));
      if (
        section !== 'general' &&
        !isEmpty(generalSectionSettings.cartographyLevel) &&
        generalSectionSettings.cartographyLevelIds.length
      ) {
        newOptions = newOptions.slice(
          newOptions.findIndex(option => option.id === generalSectionSettings.cartographyLevel) + 1
        );
      }
      setState(prevState => ({
        ...prevState,
        cartographyLevelOptions: newOptions
      }));
    } catch (e) {
      console.info(e.message);
    }
  };

  const fetchCartographyLevelShapes = async (cartographyLevel, cartographyCountries) => {
    try {
      const response = await getVariableValue(
        `cartoPyramid.${cartographyLevel}`,
        cids,
        {
          ...statsFilters(),
          cartography: {
            level0: cartographyCountries.map(country => country?.toUpperCase()),
            ...statsFilters().cartography
          }
        },
        cartographyLevelToken
      );

      setState(prevState => ({
        ...prevState,
        cartographyItemOptions: normalizeAndSortCartographyLevels(
          Object.entries(response).map(([id, name]) => ({
            id,
            name
          })),
          {
            id: 'id',
            label: 'name'
          }
        )
      }));
    } catch (e) {
      console.info(e.message);
    }
  };

  const fetchVariableValuesOptions = async variableTypeOption => {
    try {
      const variablesValues = await getVariableValue(variableTypeOption, cids, statsFilters(), variableToken);
      const possibleVariables =
        section === 'general' || !generalSectionSettings.variableValues.length
          ? variablesValues
          : formatAndSortVariableValues(variablesValues, generalSectionSettings.variableValues);
      setState(prevState => ({
        ...prevState,
        variableValuesOptions: Object.entries(possibleVariables)
          .map(([id, label]) => ({
            id,
            label
          }))
          .sort(({ label: label1, label: label2 }) => label1.localeCompare(label2))
      }));
    } catch (e) {
      console.info(e.message);
    }
  };

  const updateState = newValue => {
    setState(prevState => ({
      ...prevState,
      ...newValue
    }));
  };
  const handleCurrencyChange = newCurrency => updateState({ currentCurrency: newCurrency });
  const handleTimeStepChange = newChartTimeStep => updateState({ currentChartTimeStep: newChartTimeStep });
  const handleTableTimeStepChange = newTablesTimeStep => updateState({ currentTablesTimeStep: newTablesTimeStep });
  const handleTimeRangeChange = newTimeRange => {
    if (newTimeRange !== 'customDates') {
      const newTimeFrame = getNewTimeFrame(newTimeRange, initialDates);
      const newTimeStep = getTimeStepForTimeFrame(newTimeFrame);
      const newTablesTimeStep = Object.keys(currentSection.tablesTimeStep).reduce((acc, table) => {
        if (table !== 'footfallAttribution' || newTimeStep !== 'hour') {
          return {
            ...acc,
            [table]: newTimeStep
          };
        }
        return {
          ...acc,
          [table]: 'day'
        };
      }, {});
      updateState({
        currentChartTimeStep: newTimeStep,
        currentTablesTimeStep: newTablesTimeStep,
        currentTimeFrame: newTimeFrame,
        currentTimeRange: newTimeRange
      });
    } else {
      updateState({
        currentTimeRange: newTimeRange
      });
    }
  };
  const handleTimeFrameChange = newTimeFrame => {
    const { beginDate, endDate } = newTimeFrame;
    updateState({
      currentBeginDate: beginDate,
      currentEndDate: endDate,
      currentTimeFrame: newTimeFrame
    });
  };
  const handleTimeZoneChange = newTimeZone => updateState({ currentTimeZone: newTimeZone });
  const handleCartographyItemChange = optionsSelected =>
    updateState({
      currentCartographyLevelIds: optionsSelected.map(oS => oS.id)
    });
  const handleVariableValuesChange = variableValueOptions =>
    updateState({
      currentVariableValues: variableValueOptions.map(oS => oS.id)
    });

  const handleCartographyCountryChange = newCartographyCountries => {
    const optionsSelected = newCartographyCountries.map(oS => oS.id);
    const isSameOption =
      currentCartographyCountries &&
      optionsSelected.length === currentCartographyCountries.length &&
      currentCartographyCountries.every(country => optionsSelected.includes(country));
    if (isSameOption) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      cartographyItemOptions: [],
      cartographyLevelOptions: [],
      currentCartographyCountries: optionsSelected,
      currentCartographyLevel: '',
      currentCartographyLevelIds: []
    }));

    if (optionsSelected.length === 1) {
      fetchCartographyLevelSelectorOptions(optionsSelected);
    }
  };

  const handleCartographyLevelChange = newCartographyLevel => {
    const isSameOption = currentCartographyLevel && currentCartographyLevel === newCartographyLevel;
    if (isSameOption) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      cartographyItemOptions: [],
      currentCartographyLevel: newCartographyLevel || '',
      currentCartographyLevelIds: []
    }));

    if (newCartographyLevel) {
      fetchCartographyLevelShapes(newCartographyLevel, multiCountryReport ? currentCartographyCountries : countries);
    }
  };

  const handleVariablesTypeChange = variableTypeOption => {
    if (currentVariableType && currentVariableType === variableTypeOption) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      currentVariableType: variableTypeOption || '',
      currentVariableValues: [],
      variableValuesOptions: []
    }));
    if (variableTypeOption) {
      fetchVariableValuesOptions(variableTypeOption);
    }
  };

  const handleDatePickerChange = (newBeginDate, newEndDate) => {
    const newTimeFrame = formatTimeFrame(newBeginDate, newEndDate);
    const newTimeStep = getTimeStepForTimeFrame(newTimeFrame);
    setState(prevState => ({
      ...prevState,
      currentBeginDate: newBeginDate,
      currentChartTimeStep: newTimeStep,
      currentEndDate: newEndDate,
      currentTableTimeSteps: Object.keys(currentTablesTimeStep).reduce((acc, table) => {
        return {
          ...acc,
          [`${table}TableTimeStep`]: newTimeStep
        };
      }, {}),
      currentTimeFrame: formatTimeFrame(newBeginDate, newEndDate)
    }));
  };

  const handleUpdateViewClick = () => {
    if (!enabled) {
      return showNotification(
        'warning',
        intl.formatMessage({
          default: 'Reporting is still loading, please wait until it finishes',
          id: 'reporting.advancedFilters.waitLoad'
        })
      );
    }
    dispatch(
      reportAdvancedFilterSetSettings(
        {
          beginDate: currentBeginDate,
          cartographyCountries: currentCartographyCountries,
          cartographyLevel: currentCartographyLevel,
          cartographyLevelIds: currentCartographyLevelIds,
          cartographyLevelIdsNames: (cartographyItemOptions || [])
            .filter(opt => currentCartographyLevelIds.includes(opt.id))
            .map(opt => opt.label),
          chartTimeStep: currentChartTimeStep,
          currency: currentCurrency,
          endDate: currentEndDate,
          tablesTimeStep: currentTablesTimeStep,
          timeFrame: currentTimeFrame,
          timeRange: currentTimeRange,
          timeZone: currentTimeZone,
          variableType: currentVariableType,
          variableValues: currentVariableValues,
          variableValuesNames: (variableValuesOptions || [])
            .filter(opt => currentVariableValues.includes(opt.id))
            .map(opt => opt.label)
        },
        section
      )
    );
    dispatch(reportAdvancedFilterApplySettings(section));
  };

  const handleTrashButtonClick = () => {
    const sectionInitialSettings = section === 'general' ? initialSettings : generalSectionSettings;
    const {
      chartTimeStep,
      currency,
      timeFrame,
      timeRange = 'fromStart',
      timeStep,
      timeZone,
      variableType,
      variableValues
    } = sectionInitialSettings;
    handleTimeStepChange(chartTimeStep);
    handleTableTimeStepChange(timeStep);
    handleCurrencyChange(currency);
    handleTimeRangeChange(timeRange);
    handleTimeFrameChange(timeFrame);
    handleTimeZoneChange(timeZone);
    handleCartographyCountryChange([]);
    handleCartographyLevelChange('');
    handleCartographyItemChange([]);
    handleVariablesTypeChange(variableType);
    handleVariableValuesChange(variableValues?.map(id => ({ id })) || []);
  };

  if (ref) {
    useImperativeHandle(
      ref,
      () => ({
        applyChanges: handleUpdateViewClick,
        reset: handleTrashButtonClick
      }),
      [handleUpdateViewClick, handleTrashButtonClick]
    );
  }

  const maxDate = () => {
    const sectionTimeFrame = !section || section === 'general' ? generalSectionSettings.timeFrame : currentTimeFrame;
    if (!section || section === 'general') {
      const {
        timeFrame: { endDate }
      } = initialSettings;
      let maxDate = moment.utc(endDate);
      if (hasFootfallAttribution) {
        maxDate = moment.utc(endDate).add(15, 'days');
      }
      const today = moment.utc();
      return (maxDate.isAfter(today) ? today : maxDate).format('YYYY-MM-DD');
    }
    return sectionTimeFrame.endDate;
  };

  const minDate = () => {
    const sectionTimeFrame =
      !section || section === 'general' ? initialSettings.timeFrame : generalSectionSettings.timeFrame;
    if (hasFootfallAttribution && (!section || section === 'general' || section === 'summary')) {
      return moment.utc(sectionTimeFrame.beginDate).subtract(15, 'days').format('YYYY-MM-DD');
    }
    return sectionTimeFrame.beginDate;
  };

  const variablesTypeOptions = getVariableFilters(user, userRoles, reportingCampaigns).map(variable => ({
    id: variable,
    label: formatMessage({
      defaultMessage: variable,
      id: `reporting.advancedFilters.variables.variableType.options.${variable}`
    })
  }));

  const currencyValue = getSelectorValue(currencyOptions(), currentCurrency);
  const timeZoneValue = getSelectorValue(timeZoneOptions(), currentTimeZone);
  const timeRangeValue = getSelectorValue(timeRangeOptions(), currentTimeRange);
  const cartographyCountriesValues =
    cartographyCountryOptions.length && currentCartographyCountries.length
      ? cartographyCountryOptions.filter(oCLIO => currentCartographyCountries.includes(oCLIO.id))
      : [];
  const cartographyLevelValue = getSelectorValue(cartographyLevelOptions, currentCartographyLevel);
  const cartographyLevelIdsValues =
    cartographyItemOptions.length && currentCartographyLevelIds.length
      ? cartographyItemOptions.filter(oCLIO => currentCartographyLevelIds.includes(oCLIO.id))
      : [];
  const variableTypeValue = getSelectorValue(variablesTypeOptions, currentVariableType);
  const variablesValue =
    variableValuesOptions.length && currentVariableValues.length
      ? variableValuesOptions.filter(value => currentVariableValues.includes(value.id))
      : [];

  const saveButtonMustShouldBeEnabled = () => {
    const datesAreValid = !!currentBeginDate && !!currentEndDate;
    const cartographyLevelFilterIsValid = !cartographyLevelValue.length || !!cartographyLevelIdsValues.length;
    const multiCountryCartographyLevelFilterIsValid =
      !cartographyCountriesValues.length || (cartographyLevelValue && !!cartographyLevelIdsValues.length);
    const cartographyFiltersAreValid =
      (!multiCountryReport && cartographyLevelFilterIsValid) ||
      (multiCountryReport && multiCountryCartographyLevelFilterIsValid);
    const variableFilterIsValid = !variableTypeValue.length || !!variablesValue.length;
    return datesAreValid && cartographyFiltersAreValid && variableFilterIsValid;
  };
  const isSaveButtonEnabled = saveButtonMustShouldBeEnabled();

  const prevIsSaveButtonEnabled = usePrevious(isSaveButtonEnabled);
  if (isSaveButtonEnabled !== prevIsSaveButtonEnabled) {
    onSaveButtonDisable?.(isSaveButtonEnabled);
  }

  const advancedFilterDatePicker = () =>
    showDatePicker && (
      <div className="reportingSectionAdvancedFilter__customDatesContainer">
        <AdvancedFilterDatePicker
          beginDate={currentBeginDate}
          endDate={currentEndDate}
          maxDate={maxDate()}
          minDate={minDate()}
          onChangeDatePicker={handleDatePickerChange}
          required={!isSaveButtonEnabled}
        />
      </div>
    );

  const mainFilters = () => {
    return (
      <>
        <div className="reportingSectionAdvancedFilter__selectorsContent">
          <Selector
            className="reportingSectionAdvancedFilter__currencySelector"
            label={formatMessage({
              defaultMessage: 'Currency type',
              id: 'reporting.advancedFilters.currency.title'
            })}
            onChange={option => handleCurrencyChange(option.id)}
            options={currencyOptions()}
            type="simple"
            value={currencyValue}
          />
          <Selector
            className="reportingSectionAdvancedFilter__timezoneSelector"
            label={formatMessage({
              defaultMessage: 'Timezone',
              id: 'reporting.advancedFilters.timezone.title'
            })}
            onChange={option => handleTimeZoneChange(option.id)}
            options={timeZoneOptions()}
            type="simple"
            value={timeZoneValue}
          />
          <Selector
            className="reportingSectionAdvancedFilter__timeRangeSelector"
            label={formatMessage({
              defaultMessage: 'Date',
              id: 'reporting.advancedFilters.date.title'
            })}
            onChange={option => handleTimeRangeChange(option.id)}
            options={timeRangeOptions()}
            type="simple"
            value={timeRangeValue}
          />
          {isModal && advancedFilterDatePicker()}
        </div>
        {!isModal && advancedFilterDatePicker()}
      </>
    );
  };

  const cartographyFilters = () => {
    const cartographyLevelSelectorIsDisabled = multiCountryReport && cartographyCountriesValues.length !== 1;
    const cartographyLevelIdsSelectorIsDisabled = !cartographyItemOptions.length;
    const cartographyLevelSelectorIsRequired =
      !isSaveButtonEnabled && !cartographyLevelValue.length && multiCountryReport;
    const cartographyLevelItemSelectorIsRequired =
      !isSaveButtonEnabled && !!cartographyLevelValue.length && !cartographyLevelIdsValues.length;
    return (
      cartographyPermission && (
        <>
          {multiCountryReport && (
            <Selector
              className="reportingSectionAdvancedFilter__cartographyCountrySelector"
              isClearable={!cartographyLevelSelectorIsDisabled && !!cartographyLevelValue.length}
              isDisabled={section !== 'general' && generalSectionSettings.cartographyCountries.length}
              label={formatMessage({
                defaultMessage: 'Country',
                id: 'reporting.advancedFilters.cartography.country.title'
              })}
              onChange={handleCartographyCountryChange}
              options={cartographyCountryOptions}
              placeholder={formatMessage({
                defaultMessage: 'Select countries...',
                id: 'reporting.advancedFilters.cartography.country.placeholder'
              })}
              type="multiple"
              value={cartographyCountriesValues}
            />
          )}
          <Selector
            className="reportingSectionAdvancedFilter__cartographyLevelSelector"
            isClearable={!cartographyLevelSelectorIsDisabled && !!cartographyLevelIdsValues.length}
            isDisabled={cartographyLevelSelectorIsDisabled}
            label={formatMessage({
              defaultMessage: 'Cartography level',
              id: 'reporting.advancedFilters.cartography.level.title'
            })}
            onChange={option => handleCartographyLevelChange(option.id)}
            options={cartographyLevelOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select level...',
              id: 'reporting.advancedFilters.cartography.level.placeholder'
            })}
            required={cartographyLevelSelectorIsRequired}
            type="simple"
            value={cartographyLevelValue}
          />
          <Selector
            className={`${
              !variableSelectorsPermission
                ? 'reportingSectionAdvancedFilter__cartographyItemSelector--expanded'
                : 'reportingSectionAdvancedFilter__cartographyItemSelector'
            }`}
            isClearable={!cartographyLevelIdsSelectorIsDisabled}
            isDisabled={cartographyLevelIdsSelectorIsDisabled}
            label={formatMessage({
              defaultMessage: 'Cartography item',
              id: 'reporting.advancedFilters.cartography.item.title'
            })}
            onChange={handleCartographyItemChange}
            options={cartographyItemOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select item...',
              id: 'reporting.advancedFilters.cartography.item.placeholder'
            })}
            required={cartographyLevelItemSelectorIsRequired}
            type="multiple"
            value={cartographyLevelIdsValues}
          />
        </>
      )
    );
  };

  const variableFilters = () => {
    const variableTypeSelectorIsDisabled = section !== 'general' && generalSectionSettings.variableType;
    const variableValuesSelectorIsDisabled = !variableValuesOptions.length;
    const variableValuesSelectorIsRequired =
      !isSaveButtonEnabled && !!variableTypeValue.length && !variablesValue.length;
    return (
      variableSelectorsPermission && (
        <>
          <Selector
            className="reportingSectionAdvancedFilter__variableTypeSelector"
            isClearable={!variableTypeSelectorIsDisabled && !!variablesValue.length}
            isDisabled={variableTypeSelectorIsDisabled}
            label={formatMessage({
              defaultMessage: 'Variable type',
              id: 'reporting.advancedFilters.variables.variableType.title'
            })}
            onChange={option => handleVariablesTypeChange(option.id)}
            options={variablesTypeOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select variable',
              id: 'reporting.advancedFilters.variables.variableType.placeholder'
            })}
            type="simple"
            value={variableTypeValue}
          />
          <Selector
            className="reportingSectionAdvancedFilter__variableValueSelector"
            isClearable={!variableValuesSelectorIsDisabled}
            isDisabled={variableValuesSelectorIsDisabled}
            label={formatMessage({
              defaultMessage: 'Variable value',
              id: 'reporting.advancedFilters.variables.variableValue.title'
            })}
            onChange={handleVariableValuesChange}
            options={variableValuesOptions}
            placeholder={formatMessage({
              defaultMessage: 'Select value',
              id: 'reporting.advancedFilters.variables.variableValue.placeholder'
            })}
            required={variableValuesSelectorIsRequired}
            type="multiple"
            value={variablesValue}
          />
        </>
      )
    );
  };

  return isModal ? (
    <div className="reportingSectionAdvancedFilterModal">
      <div className="reportingSectionAdvancedFilterModal__section">
        <div className="reportingSectionAdvancedFilterModal__section-title">
          {formatMessage({
            defaultMessage: 'Main filter',
            id: 'reporting.advancedFilters.modal.mainFilters.title'
          })}
        </div>
        <div className="reportingSectionAdvancedFilterModal__section-body reportingSectionAdvancedFilterModal__mainFilters">
          {mainFilters()}
        </div>
      </div>
      {cartographyPermission && (
        <div className="reportingSectionAdvancedFilterModal__section">
          <div className="reportingSectionAdvancedFilterModal__section-title">
            {formatMessage({
              defaultMessage: 'Filter by cartography',
              id: 'reporting.advancedFilters.modal.filterByCartography.title'
            })}
          </div>
          <div className="reportingSectionAdvancedFilterModal__section-body reportingSectionAdvancedFilterModal__cartographyFilters">
            {cartographyFilters()}
          </div>
        </div>
      )}
      {variableSelectorsPermission && (
        <div className="reportingSectionAdvancedFilterModal__section">
          <div className="reportingSectionAdvancedFilterModal__section-title">
            {formatMessage({
              defaultMessage: 'Filter by a variable',
              id: 'reporting.advancedFilters.modal.filterByVariable.title'
            })}
          </div>
          <div className="reportingSectionAdvancedFilterModal__section-body reportingSectionAdvancedFilterModal__variableFilters">
            {variableFilters()}
          </div>
        </div>
      )}
    </div>
  ) : (
    <BoxV2 id="reportingSectionAdvancedFilter-box">
      <BoxV2.Body
        bgColor="background01"
        className={`reportingSectionAdvancedFilter ${className}`.trim()}
        padding="24px"
      >
        {section === 'general' ? (
          <div className="reportingSectionAdvancedFilter__header">
            <h4 className="h4 fontSemiBold">
              {formatMessage({
                defaultMessage: 'Reporting View',
                id: 'reporting.advancedFilters.title'
              })}
            </h4>
          </div>
        ) : null}
        <div className="reportingSectionAdvancedFilter__container">
          <div className="reportingSectionAdvancedFilter__selectorsFirstLine">{mainFilters()}</div>
          <div className="reportingSectionAdvancedFilter__selectorsSecondLine">
            {cartographyFilters()}
            {variableFilters()}
            <div className="reportingSectionAdvancedFilter__buttons">
              <ButtonUI
                className="reportingSectionAdvancedFilter__updateButton"
                disabled={!isSaveButtonEnabled}
                icon="fal fa-redo"
                iconPosition="left"
                onClick={handleUpdateViewClick}
                shape="squareRounded"
                size="medium"
                text="Update view"
                type="secondary"
                visible
              />
              <ButtonUI
                className="reportingSectionAdvancedFilter__trashButton"
                icon="fal fa-trash"
                onClick={handleTrashButtonClick}
                shape="squareRounded"
                size="medium"
                type="primary"
                visible
              />
            </div>
          </div>
        </div>
      </BoxV2.Body>
    </BoxV2>
  );
}

ReportingSectionAdvancedFilter.propTypes = {
  className: PropTypes.string,
  enabled: PropTypes.bool,
  initialSettings: PropTypes.shape({
    chartTimeStep: PropTypes.oneOf(['hour', 'day', 'week', 'month', 'year']).isRequired,
    currency: PropTypes.string,
    timeFrame: PropTypes.shape({
      beginDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    timeStep: PropTypes.oneOf(['hour', 'day', 'week', 'month', 'year']).isRequired,
    timeZone: PropTypes.string
  }),
  isModal: PropTypes.bool,
  onSaveButtonDisable: PropTypes.func,
  section: PropTypes.oneOf(['campaignsProgress', 'general', 'insights', 'map', 'summary', 'table'])
};

export default forwardRef(ReportingSectionAdvancedFilter);
